/**
 * VidBolt.ai - Modern Vanilla Interactive Script
 * Handles Navigation, Prompt Input, Interactive Studio Showcase,
 * Video Autoplay, and Dynamic Pricing Toggles.
 */
'use strict';

document.addEventListener('DOMContentLoaded', () => {

    /* ==========================================================================
       1. Dynamic Footer Year
       ========================================================================== */
    const yearSpan = document.getElementById('current-year');
    if (yearSpan) {
        yearSpan.textContent = new Date().getFullYear();
    }

    /* ==========================================================================
       2. Mobile Navigation Toggle
       ========================================================================== */
    const mobileBtn = document.getElementById('mobile-menu-btn');
    const mobileOverlay = document.getElementById('mobile-nav-overlay');
    const mobileLinks = document.querySelectorAll('.mobile-nav-link');

    const mobileCloseBtn = document.getElementById('mobile-nav-close');

    if (mobileBtn && mobileOverlay) {
        const ICON_MENU = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="4" x2="20" y1="12" y2="12"/><line x1="4" x2="20" y1="6" y2="6"/><line x1="4" x2="20" y1="18" y2="18"/></svg>`;
        const ICON_CLOSE = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>`;

        function setNavOpen(open) {
            mobileOverlay.classList.toggle('open', open);
            mobileBtn.setAttribute('aria-expanded', String(open));
            mobileBtn.innerHTML = open ? ICON_CLOSE : ICON_MENU;
            document.body.classList.toggle('nav-open', open);
        }

        mobileBtn.addEventListener('click', () => {
            setNavOpen(!mobileOverlay.classList.contains('open'));
        });

        if (mobileCloseBtn) {
            mobileCloseBtn.addEventListener('click', () => setNavOpen(false));
        }

        // Tapping the backdrop (anywhere that isn't a link or the close button) closes it.
        mobileOverlay.addEventListener('click', (e) => {
            if (e.target === mobileOverlay) setNavOpen(false);
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && mobileOverlay.classList.contains('open')) {
                setNavOpen(false);
            }
        });

        mobileLinks.forEach(link => {
            link.addEventListener('click', () => setNavOpen(false));
        });
    }

    /* ==========================================================================
    /* ==========================================================================
       3. Toast Notification Helper
       ========================================================================== */
    function showToast(message) {
        const toast = document.getElementById('toast');
        if (!toast) return;
        toast.textContent = message;
        toast.classList.add('show');
        setTimeout(() => {
            toast.classList.remove('show');
        }, 4500);
    }

    /* ==========================================================================
       4. Smooth Scrolling with Offset & Scroll-to-Top
       ========================================================================== */
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function(e) {
            const targetId = this.getAttribute('href').slice(1);
            if (!targetId || targetId === 'top') {
                e.preventDefault();
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
                const promptInput = document.getElementById('hero-prompt-input');
                if (promptInput) {
                    setTimeout(() => promptInput.focus(), 300);
                }
                return;
            }

            const targetElement = document.getElementById(targetId);
            if (targetElement) {
                e.preventDefault();
                const navHeight = 76;
                const targetPosition = targetElement.getBoundingClientRect().top + window.scrollY - navHeight;
                window.scrollTo({
                    top: Math.max(0, targetPosition),
                    behavior: 'smooth'
                });
            }
        });
    });

    /* ==========================================================================
       5. Hero Prompt Input & Suggestions
       ========================================================================== */
    const promptInput = document.getElementById('hero-prompt-input');
    const promptForm = document.getElementById('hero-prompt-form');
    const modeTabs = document.querySelectorAll('.mode-tab');
    const suggestionChips = document.querySelectorAll('.suggestion-chip');
    let currentMode = 'ai';

    modeTabs.forEach(tab => {
        tab.addEventListener('click', () => {
            modeTabs.forEach(t => t.classList.remove('active'));
            tab.classList.add('active');
            currentMode = tab.dataset.mode || 'ai';
            if (promptInput) {
                promptInput.placeholder = currentMode === 'script'
                    ? 'Paste your video script here with dialog or narration...'
                    : 'A vertical video about a cyberpunk detective solving a crime in Neo Tokyo...';
            }
        });
    });

    suggestionChips.forEach(chip => {
        chip.addEventListener('click', () => {
            const promptText = chip.dataset.prompt;
            if (promptInput && promptText) {
                promptInput.value = promptText;
                promptInput.focus();
            }
        });
    });

    if (promptForm) {
        promptForm.addEventListener('submit', (e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: 'smooth' });
            showToast("🚀 VidBolt is live — and we're rebuilding the studio around AI agents, with premium video, audio and music models. Get in touch via our contact page for early access to the new version.");
        });
    }

    /* ==========================================================================
       5. Hero Video Autoplay via Intersection Observer
       ========================================================================== */
    const allVideos = document.querySelectorAll('.marquee-card video, .hero-video-item');
    if ('IntersectionObserver' in window && allVideos.length > 0) {
        const videoObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                const vid = entry.target;
                if (entry.isIntersecting) {
                    vid.muted = true;
                    vid.playsInline = true;
                    vid.play().catch(() => {});
                } else {
                    vid.pause();
                }
            });
        }, { threshold: 0.1 });

        allVideos.forEach(v => videoObserver.observe(v));
    }

    /* ==========================================================================
       6. Journey Animation — auto-advancing 5-phase product walkthrough.
          Vanilla port of features/home/feature-showcase-section/journey-animation.tsx
          (same phase durations, sub-step timings, states and visuals).
       ========================================================================== */
    initJourneyAnimation();

    function initJourneyAnimation() {
        const root = document.getElementById('journey-animation');
        const stepsEl = document.getElementById('journey-steps');
        const panelEl = document.getElementById('journey-panel-inner');
        if (!root || !stepsEl || !panelEl) return;

        const PHASE_CONFIGS = {
            1: { label: 'Enter your idea', duration: 5500 },
            2: { label: 'AI generates your scenes', duration: 10000 },
            3: { label: 'Add your own scene', duration: 8000 },
            4: { label: 'Animate your scenes', duration: 8500 },
            5: { label: 'Final Video', duration: 6000 }
        };
        const ALL_PHASES = [1, 2, 3, 4, 5];

        const PROMPT_TEXT = 'A vertical video about a mother with her baby in pixar style in deep male voice';
        const SCRIPT_LINES = [
            'From the soft lullabies whispered in the quiet of night, to the tender touch that soothed every cry, a mother’s love blossoms.',
            'She feeds, she cleans, her days a tireless ballet of devotion.',
            'She teaches, she guides, her heart swelling with pride as her child takes their first wobbly steps.'
        ];
        const EXTRA_LINE = 'She watches, her eyes full of boundless love, as they explore the world and speak their first words.';
        const MOTION_PROMPT_TEXT = 'Slow zoom into the mother’s face as she smiles';
        const SETTINGS_CHIPS = [
            { label: 'English', key: 'lang' },
            { label: '9:16', key: 'aspect' },
            { label: 'Audio', key: 'audio', hasWaveform: true },
            { label: 'Anime', key: 'style' }
        ];
        // Read the bundler-resolved (fingerprinted) URLs out of the markup.
        const FALLBACK_IMAGES = [
            'assets/images/scene-1.jpg',
            'assets/images/scene-2.jpg',
            'assets/images/scene-3.jpg',
            'assets/images/scene-4.jpg'
        ];
        const preloadHost = document.getElementById('journey-scene-images');
        const declared = preloadHost
            ? Array.prototype.map.call(preloadHost.querySelectorAll('img'), (img) => img.src)
            : [];
        const SCENE_IMAGES = declared.length === 4 ? declared : FALLBACK_IMAGES;

        const ICONS = {
            check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>',
            plus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="M12 5v14"/></svg>',
            play: '<svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>',
            film: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"/><path d="M7 3v18M17 3v18M3 7.5h4M17 7.5h4M3 12h18M3 16.5h4M17 16.5h4"/></svg>',
            image: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21"/></svg>',
            clock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
            loader: '<svg class="jr-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>'
        };

        // ---- helpers --------------------------------------------------------
        const el = (tag, cls, html) => {
            const n = document.createElement(tag);
            if (cls) n.className = cls;
            if (html != null) n.innerHTML = html;
            return n;
        };
        const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

        let timers = [];
        function clearTimers() {
            timers.forEach(clearTimeout);
            timers = [];
        }
        function schedule(fn, delay) {
            timers.push(setTimeout(fn, delay));
        }

        let typingTimer = null;
        function typeInto(text, speed, onChar, onDone) {
            let i = 0;
            clearTimeout(typingTimer);
            const step = () => {
                i += 1;
                onChar(text.substring(0, i));
                if (i < text.length) {
                    typingTimer = setTimeout(step, speed);
                } else if (onDone) {
                    onDone();
                }
            };
            onChar('');
            typingTimer = setTimeout(step, speed);
        }

        // ---- state ----------------------------------------------------------
        let phase = 1;
        let completed = new Set();
        let running = false;
        // Per-phase DOM references, rebuilt on every phase change.
        let refs = {};

        // ---- step indicator -------------------------------------------------
        const stepNodes = ALL_PHASES.map((p) => {
            const item = el('div', 'journey-step');
            const dot = el('div', 'journey-step-dot');
            const label = el('span', 'journey-step-label', esc(PHASE_CONFIGS[p].label));
            item.appendChild(dot);
            item.appendChild(label);
            stepsEl.appendChild(item);
            return { item, dot };
        });

        function renderSteps() {
            stepNodes.forEach(({ item, dot }, i) => {
                const p = ALL_PHASES[i];
                const isActive = p === phase;
                const isDone = completed.has(p);
                item.classList.toggle('is-active', isActive);
                item.classList.toggle('is-done', isDone);
                dot.innerHTML = isDone ? ICONS.check : '';
            });
            // Centre the active step when the row scrolls horizontally (mobile).
            const active = stepNodes[phase - 1].item;
            if (stepsEl.scrollWidth > stepsEl.clientWidth) {
                const left = active.offsetLeft - stepsEl.clientWidth / 2 + active.offsetWidth / 2;
                stepsEl.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
            }
        }

        // ---- builders -------------------------------------------------------
        function buildPromptBlock() {
            const block = el('div', 'jr-block');
            block.appendChild(el('div', 'jr-eyebrow', 'Your idea'));
            const row = el('div', 'jr-prompt-row');
            const box = el('div', 'jr-prompt-box');
            const text = el('span');
            box.appendChild(text);
            const btn = el('button', 'jr-generate', 'Generate');
            btn.type = 'button';
            btn.tabIndex = -1;
            btn.setAttribute('aria-hidden', 'true');
            row.appendChild(box);
            row.appendChild(btn);
            block.appendChild(row);
            refs.promptText = text;
            refs.generateBtn = btn;
            return block;
        }

        function buildChips() {
            const row = el('div', 'jr-block jr-chips');
            refs.chips = SETTINGS_CHIPS.map((chip) => {
                const node = el('div', 'jr-chip');
                node.dataset.key = chip.key;
                row.appendChild(node);
                return node;
            });
            return row;
        }

        function renderChip(i, state) {
            const chip = refs.chips && refs.chips[i];
            if (!chip) return;
            const def = SETTINGS_CHIPS[i];
            chip.classList.toggle('is-shimmer', state === 'shimmer');
            chip.classList.toggle('is-visible', state === 'visible');
            let inner = '';
            if (state === 'shimmer' && def.hasWaveform) {
                inner += '<span class="jr-wave' + (waveActive ? ' is-active' : '') + '">' +
                    [8, 14, 10, 16, 12].map((h, b) =>
                        '<i style="height:' + (waveActive ? h : 3) + 'px;animation-delay:' + (b * 0.07) + 's"></i>'
                    ).join('') + '</span>';
            }
            if (state === 'visible') inner += ICONS.check;
            inner += '<span>' + esc(def.label) + '</span>';
            chip.innerHTML = inner;
        }

        function buildScript() {
            const wrap = el('div', 'jr-script');
            const body = el('div', 'jr-script-body');
            const holder = el('span');
            body.appendChild(holder);
            wrap.appendChild(body);
            refs.scriptHolder = holder;
            return wrap;
        }

        function renderScript() {
            const holder = refs.scriptHolder;
            if (!holder) return;
            let html = '';
            SCRIPT_LINES.forEach((line, i) => {
                const state = lineStates[i];
                if (state === 'hidden') return;
                if (i > 0 && lineStates[i - 1] !== 'hidden') html += ' ';
                html += '<span class="jr-line state-' + state + '">' + esc(line) + '</span>';
            });
            if (extraState !== 'hidden') {
                const text = extraState === 'typing' ? typedExtra : EXTRA_LINE;
                html += ' <span class="jr-extra">';
                if (extraState === 'selecting') {
                    html += '<span class="jr-extra-sel">' + esc(EXTRA_LINE.substring(0, selectionProgress)) + '</span>';
                    html += '<span class="jr-extra-rest">' + esc(EXTRA_LINE.substring(selectionProgress)) + '</span>';
                } else {
                    html += '<span class="jr-line state-' + (extraState === 'assigned' ? 'assigned' : 'unassigned') + '">' + esc(text) + '</span>';
                }
                if (extraState === 'typing') html += '<span class="jr-caret-sm"></span>';
                html += '</span>';
                if (showAddScene) {
                    html += '<span class="jr-add-scene">' + ICONS.plus + '<span>Add Scene</span></span>';
                }
            }
            holder.innerHTML = html;
        }

        function buildStoryboard(count) {
            const strip = el('div', 'jr-storyboard');
            refs.cards = [];
            for (let i = 0; i < count; i++) {
                const card = el('div', 'jr-card');
                card.style.display = 'none';
                const img = el('img');
                img.src = SCENE_IMAGES[i] || SCENE_IMAGES[0];
                img.alt = 'Scene ' + (i + 1);
                img.loading = 'lazy';
                img.decoding = 'async';
                card.appendChild(img);
                card.appendChild(el('span', 'jr-card-num', String(i + 1)));
                strip.appendChild(card);
                refs.cards.push(card);
            }
            return strip;
        }

        function renderStoryboard() {
            if (!refs.cards) return;
            refs.cards.forEach((card, i) => {
                const state = cardStates[i] || 'hidden';
                card.style.display = state === 'hidden' ? 'none' : '';
                card.classList.toggle('is-shimmer', state === 'shimmer');

                let aurora = card.querySelector('.jr-card-aurora');
                if (state === 'shimmer' && !aurora) {
                    card.insertBefore(el('div', 'jr-card-aurora'), card.firstChild.nextSibling);
                } else if (state !== 'shimmer' && aurora) {
                    aurora.remove();
                }

                const wantsBadge = !!playBadges[i];
                const badge = card.querySelector('.jr-card-play');
                if (wantsBadge && !badge) {
                    card.appendChild(el('div', 'jr-card-play', '<span>' + ICONS.play + '</span>'));
                } else if (!wantsBadge && badge) {
                    badge.remove();
                }
            });
        }

        function buildInspector() {
            const box = el('div', 'jr-inspector');
            box.innerHTML =
                '<div class="jr-insp-tabs">' +
                    '<button type="button" tabindex="-1" class="jr-insp-tab" data-tab="visual">' + ICONS.image + 'Visual</button>' +
                    '<button type="button" tabindex="-1" class="jr-insp-tab" data-tab="motion">' + ICONS.film + 'Motion</button>' +
                '</div>' +
                '<div class="jr-insp-head">' +
                    '<div class="jr-insp-scene"><b>Scene #1</b><span>3.2s</span></div>' +
                    '<div class="jr-insp-time">' + ICONS.clock + '<span>0:00 &ndash; 0:03</span></div>' +
                '</div>' +
                '<div class="jr-insp-visual"><div><img src="' + SCENE_IMAGES[0] + '" alt="Scene 1 visual" loading="lazy" decoding="async"></div></div>' +
                '<div class="jr-insp-body" hidden>' +
                    '<div class="jr-insp-prompt-head">' +
                        '<span>Motion Prompt <em>(optional)</em></span>' +
                        '<button type="button" tabindex="-1" class="jr-ai-draft"><span>&#10022;</span>AI Draft</button>' +
                    '</div>' +
                    '<div class="jr-insp-textarea is-placeholder"></div>' +
                    '<p class="jr-insp-help">Use AI Draft to generate a prompt, or leave blank and animate to auto-generate.</p>' +
                    '<button type="button" tabindex="-1" class="jr-animate-btn"></button>' +
                '</div>';
            refs.inspTabs = box.querySelectorAll('.jr-insp-tab');
            refs.inspVisual = box.querySelector('.jr-insp-visual');
            refs.inspBody = box.querySelector('.jr-insp-body');
            refs.aiDraft = box.querySelector('.jr-ai-draft');
            refs.motionBox = box.querySelector('.jr-insp-textarea');
            refs.animateBtn = box.querySelector('.jr-animate-btn');
            return box;
        }

        function renderInspector() {
            if (!refs.inspTabs) return;
            refs.inspTabs.forEach((t) => {
                t.classList.toggle('is-active', t.dataset.tab === inspectorTab);
            });
            refs.inspVisual.hidden = inspectorTab !== 'visual';
            refs.inspBody.hidden = inspectorTab !== 'motion';
            refs.aiDraft.classList.toggle('is-pressed', aiDraftPressed);

            const caret = motionTyping ? '<span class="jr-caret-xs"></span>' : '';
            if (typedMotion.length > 0) {
                refs.motionBox.classList.remove('is-placeholder');
                refs.motionBox.innerHTML = '<span>' + esc(typedMotion) + caret + '</span>';
            } else {
                refs.motionBox.classList.add('is-placeholder');
                refs.motionBox.innerHTML = '<span>Describe camera movement, subject motion...' + caret + '</span>';
            }

            refs.animateBtn.className = 'jr-animate-btn state-' + animateBtnState;
            if (animateBtnState === 'animating') {
                refs.animateBtn.innerHTML = ICONS.loader + 'Animating...';
            } else if (animateBtnState === 'done') {
                refs.animateBtn.innerHTML = ICONS.play + 'Re-animate Scene';
            } else {
                refs.animateBtn.innerHTML = ICONS.play + 'Animate Scene';
            }
        }

        function renderBulk() {
            const holder = refs.bulkHolder;
            if (!holder) return;
            if (!showBulkAnimate) {
                holder.innerHTML = '';
                return;
            }
            const icon = bulkAnimating ? ICONS.loader : ICONS.film;
            const label = bulkAnimating ? 'Animating 3...' : 'Animate 3 Scenes';
            holder.innerHTML = '<button type="button" tabindex="-1" class="jr-bulk-btn' +
                (bulkPressed ? ' is-pressed' : '') + '">' + icon + '<span>' + label + '</span></button>';
        }

        function buildRender() {
            const wrap = el('div', 'jr-render');
            refs.renderHolder = wrap;
            return wrap;
        }

        function renderRenderPhase() {
            const wrap = refs.renderHolder;
            if (!wrap) return;
            if (renderSubStep === 0) {
                wrap.innerHTML = '<button type="button" tabindex="-1" class="jr-render-btn">Render Video</button>';
            } else if (renderSubStep < 3) {
                wrap.innerHTML =
                    '<div class="jr-progress">' +
                        '<div class="jr-progress-row"><span>Rendering...</span><span class="jr-progress-pct">0%</span></div>' +
                        '<div class="jr-progress-bar"><div class="jr-progress-fill"></div></div>' +
                    '</div>';
                animateProgress(wrap, 2000);
            } else {
                wrap.innerHTML =
                    '<div class="jr-final">' +
                        '<div class="jr-final-frame">' +
                            '<img src="' + SCENE_IMAGES[0] + '" alt="Rendered video preview" loading="lazy" decoding="async">' +
                            '<div class="jr-final-play"><span>' + ICONS.play + '</span></div>' +
                        '</div>' +
                        '<b>Video Ready</b>' +
                    '</div>';
            }
        }

        let rafId = 0;
        function animateProgress(scope, durationMs) {
            cancelAnimationFrame(rafId);
            const pctEl = scope.querySelector('.jr-progress-pct');
            const fillEl = scope.querySelector('.jr-progress-fill');
            if (!pctEl || !fillEl) return;
            let start = null;
            const tick = (now) => {
                if (start === null) start = now;
                const pct = Math.min(Math.round(((now - start) / durationMs) * 100), 100);
                pctEl.textContent = pct + '%';
                fillEl.style.width = pct + '%';
                if (pct < 100) rafId = requestAnimationFrame(tick);
            };
            rafId = requestAnimationFrame(tick);
        }

        // ---- per-phase mutable state ----------------------------------------
        let lineStates, cardStates, playBadges, extraState, typedExtra, selectionProgress,
            showAddScene, waveActive, inspectorTab, typedMotion, motionTyping, aiDraftPressed,
            animateBtnState, showBulkAnimate, bulkPressed, bulkAnimating, renderSubStep;

        function resetPhaseState() {
            lineStates = ['hidden', 'hidden', 'hidden'];
            cardStates = ['hidden', 'hidden', 'hidden', 'hidden'];
            playBadges = [false, false, false, false];
            extraState = 'hidden';
            typedExtra = '';
            selectionProgress = 0;
            showAddScene = false;
            waveActive = false;
            inspectorTab = 'visual';
            typedMotion = '';
            motionTyping = false;
            aiDraftPressed = false;
            animateBtnState = 'idle';
            showBulkAnimate = false;
            bulkPressed = false;
            bulkAnimating = false;
            renderSubStep = 0;
        }

        // ---- phase mounting --------------------------------------------------
        function mountPhase() {
            refs = {};
            panelEl.innerHTML = '';
            const stage = el('div', 'jr-stage');
            panelEl.appendChild(stage);

            // Carried-forward state, mirroring the React "effective*" selectors.
            if (phase >= 3) lineStates = ['assigned', 'assigned', 'assigned'];
            if (phase === 3) cardStates = ['visible', 'visible', 'visible', 'hidden'];
            if (phase === 4) cardStates = ['visible', 'visible', 'visible', 'visible'];
            if (phase === 5) playBadges = [true, true, true, true];

            if (phase === 1) {
                stage.appendChild(buildPromptBlock());
            }

            if (phase === 2 || phase === 3) {
                const chips = buildChips();
                stage.appendChild(chips);
                const chipState = phase >= 3 ? 'visible' : 'hidden';
                refs.chips.forEach((_, i) => renderChip(i, chipState));

                stage.appendChild(buildScript());
                renderScript();

                stage.appendChild(buildStoryboard(phase >= 3 ? 4 : 3));
                renderStoryboard();
            }

            if (phase === 4) {
                const layout = el('div', 'jr-animate');
                const left = el('div', 'jr-animate-left');
                const bulkHolder = el('div');
                refs.bulkHolder = bulkHolder;
                left.appendChild(bulkHolder);
                left.appendChild(buildStoryboard(4));
                layout.appendChild(left);
                const right = el('div', 'jr-animate-right');
                right.appendChild(buildInspector());
                layout.appendChild(right);
                stage.appendChild(layout);
                renderStoryboard();
                renderInspector();
                renderBulk();
            }

            if (phase === 5) {
                stage.appendChild(buildRender());
                renderRenderPhase();
            }
        }

        // ---- phase orchestration (timings copied from journey-animation.tsx) --
        function runPhase() {
            clearTimers();
            clearTimeout(typingTimer);
            cancelAnimationFrame(rafId);
            mountPhase();
            renderSteps();

            const duration = PHASE_CONFIGS[phase].duration;

            if (phase === 1) {
                typeInto(PROMPT_TEXT, 55, (t) => {
                    refs.promptText.innerHTML = esc(t) + '<span class="jr-caret"></span>';
                }, () => {
                    refs.promptText.textContent = PROMPT_TEXT;
                    refs.generateBtn.classList.add('is-ready');
                });
                schedule(() => {
                    refs.generateBtn.classList.remove('is-ready');
                    refs.generateBtn.classList.add('is-clicked');
                }, duration - 600);
            }

            if (phase === 2) {
                // Step 1: settings chips shimmer, then resolve with a tick.
                for (let i = 0; i < 4; i++) {
                    const isAudio = !!SETTINGS_CHIPS[i].hasWaveform;
                    schedule(() => {
                        if (isAudio) waveActive = true;
                        renderChip(i, 'shimmer');
                    }, 200 + i * 450);
                    schedule(() => {
                        if (isAudio) waveActive = false;
                        renderChip(i, 'visible');
                    }, 800 + i * 450);
                }

                // Step 2: script lines fade in.
                const scriptStart = 2800;
                for (let i = 0; i < 3; i++) {
                    schedule(() => { lineStates[i] = 'fade-in'; renderScript(); }, scriptStart + i * 200);
                    schedule(() => { lineStates[i] = 'unassigned'; renderScript(); }, scriptStart + i * 200 + 500);
                }

                // Step 3: lines get assigned to scenes, storyboard cards appear.
                const sceneStart = 4500;
                for (let i = 0; i < 3; i++) {
                    schedule(() => { lineStates[i] = 'assigning'; renderScript(); }, sceneStart + i * 1000);
                    schedule(() => { lineStates[i] = 'assigned'; renderScript(); }, sceneStart + i * 1000 + 400);
                    schedule(() => { cardStates[i] = 'shimmer'; renderStoryboard(); }, sceneStart + i * 1000 + 500);
                    schedule(() => { cardStates[i] = 'visible'; renderStoryboard(); }, sceneStart + i * 1000 + 900);
                }
            }

            if (phase === 3) {
                schedule(() => {
                    extraState = 'typing';
                    typeInto(EXTRA_LINE, 20, (t) => { typedExtra = t; renderScript(); });
                }, 400);

                const typingDone = 2600;
                schedule(() => {
                    clearTimeout(typingTimer);
                    typedExtra = EXTRA_LINE;
                    extraState = 'typed';
                    renderScript();
                }, typingDone);

                // Progressive text selection.
                const selStart = typingDone + 600;
                const selDuration = 1800;
                const selSteps = 25;
                for (let i = 1; i <= selSteps; i++) {
                    const progress = Math.round((EXTRA_LINE.length * i) / selSteps);
                    schedule(() => {
                        extraState = 'selecting';
                        selectionProgress = progress;
                        renderScript();
                    }, selStart + (selDuration * i) / selSteps);
                }

                schedule(() => {
                    selectionProgress = EXTRA_LINE.length;
                    showAddScene = true;
                    renderScript();
                }, selStart + selDuration + 300);

                schedule(() => {
                    showAddScene = false;
                    extraState = 'assigned';
                    renderScript();
                    cardStates[3] = 'shimmer';
                    renderStoryboard();
                }, selStart + selDuration + 1200);

                schedule(() => {
                    cardStates[3] = 'visible';
                    renderStoryboard();
                }, selStart + selDuration + 2000);
            }

            if (phase === 4) {
                schedule(() => { inspectorTab = 'motion'; renderInspector(); }, 400);
                schedule(() => { aiDraftPressed = true; renderInspector(); }, 800);
                schedule(() => { aiDraftPressed = false; renderInspector(); }, 950);
                schedule(() => {
                    motionTyping = true;
                    typeInto(MOTION_PROMPT_TEXT, 25, (t) => { typedMotion = t; renderInspector(); });
                }, 1000);
                schedule(() => {
                    clearTimeout(typingTimer);
                    typedMotion = MOTION_PROMPT_TEXT;
                    motionTyping = false;
                    renderInspector();
                }, 2300);
                schedule(() => { animateBtnState = 'pressed'; renderInspector(); }, 2500);
                schedule(() => { animateBtnState = 'animating'; renderInspector(); }, 2700);
                schedule(() => { cardStates[0] = 'shimmer'; renderStoryboard(); }, 3200);
                schedule(() => {
                    cardStates[0] = 'visible';
                    playBadges[0] = true;
                    renderStoryboard();
                    animateBtnState = 'done';
                    renderInspector();
                }, 4500);
                schedule(() => { showBulkAnimate = true; renderBulk(); }, 5200);
                schedule(() => { bulkPressed = true; renderBulk(); }, 5400);
                schedule(() => { bulkPressed = false; bulkAnimating = true; renderBulk(); }, 5600);
                schedule(() => {
                    cardStates[1] = cardStates[2] = cardStates[3] = 'shimmer';
                    renderStoryboard();
                }, 5800);
                schedule(() => {
                    cardStates = ['visible', 'visible', 'visible', 'visible'];
                    playBadges = [true, true, true, true];
                    renderStoryboard();
                    bulkAnimating = false;
                    showBulkAnimate = false;
                    renderBulk();
                }, 7200);
            }

            if (phase === 5) {
                schedule(() => { renderSubStep = 1; renderRenderPhase(); }, 1000);
                schedule(() => { renderSubStep = 3; renderRenderPhase(); }, 3200);
            }

            // Advance to the next phase.
            schedule(() => {
                resetPhaseState();
                if (phase >= 5) {
                    completed = new Set();
                    phase = 1;
                } else {
                    completed.add(phase);
                    phase += 1;
                }
                runPhase();
            }, duration);
        }

        function start() {
            if (running) return;
            running = true;
            resetPhaseState();
            phase = 1;
            completed = new Set();
            runPhase();
        }

        function stop() {
            running = false;
            clearTimers();
            clearTimeout(typingTimer);
            cancelAnimationFrame(rafId);
        }

        // Only animate while the section is on screen; pause when scrolled away
        // or when the tab is hidden, so the loop never burns cycles unseen.
        if (typeof IntersectionObserver === 'undefined') {
            start();
        } else {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        if (!document.hidden) start();
                    } else {
                        stop();
                    }
                });
            }, { threshold: 0.15 });
            observer.observe(root);

            document.addEventListener('visibilitychange', () => {
                if (document.hidden) {
                    stop();
                } else if (root.getBoundingClientRect().top < window.innerHeight &&
                           root.getBoundingClientRect().bottom > 0) {
                    start();
                }
            });
        }

        // Render an initial frozen state so the panel is never empty before the
        // observer fires.
        resetPhaseState();
        mountPhase();
        renderSteps();
    }

    /* ==========================================================================
       7. Pricing Monthly / Annual Toggle
       ========================================================================== */
    const monthlyToggleBtn = document.getElementById('toggle-monthly');
    const annualToggleBtn = document.getElementById('toggle-annual');
    const pricingCards = document.querySelectorAll('.pricing-card');

    const PLAN_PRICES = {
        free: { monthly: 0, annual: 0, savings: 0 },
        starter: { monthly: 12, annual: 10, savings: 24 },
        pro: { monthly: 29, annual: 23, savings: 72 },
        ultra: { monthly: 49, annual: 39, savings: 120 }
    };

    function updatePricing(billingCycle) {
        const isAnnual = billingCycle === 'annual';

        if (monthlyToggleBtn && annualToggleBtn) {
            monthlyToggleBtn.classList.toggle('active', !isAnnual);
            annualToggleBtn.classList.toggle('active', isAnnual);
        }

        pricingCards.forEach(card => {
            const planKey = card.dataset.plan;
            const priceEl = card.querySelector('.price-amount');
            const savingsEl = card.querySelector('.annual-savings');

            if (planKey && PLAN_PRICES[planKey] && priceEl) {
                const planData = PLAN_PRICES[planKey];
                const price = isAnnual ? planData.annual : planData.monthly;
                priceEl.textContent = `$${price}`;

                if (savingsEl && planData.savings > 0) {
                    savingsEl.textContent = `Save $${planData.savings}/year with annual billing`;
                }

                card.classList.toggle('show-annual', isAnnual && planData.savings > 0);
            }
        });
    }

    if (monthlyToggleBtn) {
        monthlyToggleBtn.addEventListener('click', () => updatePricing('monthly'));
    }
    if (annualToggleBtn) {
        annualToggleBtn.addEventListener('click', () => updatePricing('annual'));
    }

});